import React, { useMemo } from 'react';
import Timestamp from './common/Timestamp';
import './TrendRangeSlider.scss';

interface TrendRangeSliderProps {
    /** Full window lower bound (e.g. now-1y). */
    windowStart: Date;
    /** Full window upper bound (typically now / latest snapshot). */
    windowEnd: Date;
    /** Currently-selected sub-range start/end (within the window). */
    selectedStart: Date;
    selectedEnd: Date;
    /** Emits the new sub-range when either thumb moves. */
    onChange: (start: Date, end: Date) => void;
    /** Human label for the full window (e.g. "Last 1 Year"). */
    windowLabel?: string;
}

// Discrete steps across the window. Snapshots are ~15-min, but the backend aligns the
// requested range to the actual snapshots, so a fixed step count keeps the control smooth
// regardless of window size.
const STEPS = 240;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** "2 days", "3 hours", "45 minutes" for a duration in ms (rounded, nearest sensible unit). */
const humanizeDuration = (ms: number): string => {
    if (ms >= DAY_MS) {
        const d = Math.max(1, Math.round(ms / DAY_MS));
        return `${d} day${d === 1 ? '' : 's'}`;
    }
    if (ms >= HOUR_MS) {
        const h = Math.max(1, Math.round(ms / HOUR_MS));
        return `${h} hour${h === 1 ? '' : 's'}`;
    }
    const m = Math.max(1, Math.round(ms / (60 * 1000)));
    return `${m} minute${m === 1 ? '' : 's'}`;
};

/**
 * Plain-language description of the selected range relative to "now" (windowEnd). When the end
 * thumb is at (or within a snapshot interval of) now, reads "Last N days"; otherwise reads
 * "N days ago -> M days ago" so the user understands the span and its offset.
 */
const describeRelativeRange = (start: Date, end: Date, now: Date): string => {
    const endOffset = now.getTime() - end.getTime();
    const startOffset = now.getTime() - start.getTime();
    // Treat an end within ~1% of the window (or <= 1h) of "now" as "up to now".
    const atNow = endOffset <= HOUR_MS || endOffset <= (startOffset * 0.01);
    if (atNow) {
        return `Last ${humanizeDuration(Math.max(0, startOffset))}`;
    }
    return `${humanizeDuration(startOffset)} ago → ${humanizeDuration(endOffset)} ago`;
};

/**
 * Dual-thumb range slider bounded by the selected timeWindow ([windowStart, windowEnd]).
 * Dragging either thumb selects a sub-range; the parent refetches trend snapshots for it.
 */
const TrendRangeSlider: React.FC<TrendRangeSliderProps> = ({
    windowStart,
    windowEnd,
    selectedStart,
    selectedEnd,
    onChange,
    windowLabel,
}) => {
    const windowStartMs = windowStart.getTime();
    const windowEndMs = windowEnd.getTime();
    const span = Math.max(1, windowEndMs - windowStartMs);

    const toStep = (d: Date) => {
        const clamped = Math.min(windowEndMs, Math.max(windowStartMs, d.getTime()));
        return Math.round(((clamped - windowStartMs) / span) * STEPS);
    };
    const toDate = (step: number) => new Date(windowStartMs + (step / STEPS) * span);

    const startStep = useMemo(() => toStep(selectedStart), [selectedStart, windowStartMs, windowEndMs]);
    const endStep = useMemo(() => toStep(selectedEnd), [selectedEnd, windowStartMs, windowEndMs]);

    const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = Math.min(Number(e.target.value), endStep - 1);
        onChange(toDate(Math.max(0, next)), selectedEnd);
    };
    const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = Math.max(Number(e.target.value), startStep + 1);
        onChange(selectedStart, toDate(Math.min(STEPS, next)));
    };

    const leftPct = (startStep / STEPS) * 100;
    const rightPct = (endStep / STEPS) * 100;

    const relativeLabel = useMemo(
        () => describeRelativeRange(selectedStart, selectedEnd, windowEnd),
        [selectedStart, selectedEnd, windowEnd],
    );

    return (
        <div className="trend-range-slider">
            <div className="trend-range-slider__labels">
                <span className="trend-range-slider__window">{windowLabel || 'Selected window'}</span>
                <span className="trend-range-slider__relative">{relativeLabel}</span>
                <span className="trend-range-slider__range">
                    <Timestamp date={selectedStart} /> &rarr; <Timestamp date={selectedEnd} />
                </span>
            </div>
            <div className="trend-range-slider__track-wrap">
                <div className="trend-range-slider__track" />
                <div
                    className="trend-range-slider__fill"
                    style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
                />
                <input
                    type="range"
                    min={0}
                    max={STEPS}
                    value={startStep}
                    onChange={handleStartChange}
                    aria-label="Trend range start"
                    className="trend-range-slider__thumb trend-range-slider__thumb--start"
                />
                <input
                    type="range"
                    min={0}
                    max={STEPS}
                    value={endStep}
                    onChange={handleEndChange}
                    aria-label="Trend range end"
                    className="trend-range-slider__thumb trend-range-slider__thumb--end"
                />
            </div>
            <div className="trend-range-slider__bounds">
                <span><Timestamp date={windowStart} /></span>
                <span><Timestamp date={windowEnd} /></span>
            </div>
        </div>
    );
};

export default TrendRangeSlider;
